import React from 'react';
import { useTranslation } from 'react-i18next';

import { InternalLink } from 'src/api/contentful/contentTypes.cloverdotcom';
import { FlatEntry } from 'src/services/cms/flattenContentful';
import { isChildrenDefined } from 'src/utils/ChildrenCheck';
import { getUrlWithParamsOrPdpRedirect, shouldRedirectExistingMerchant } from 'src/utils/ExistingMerchant';
import { getEntryDataAttributes } from 'src/utils/GetEntryDataAttributes';
import { testAttributeWithPrefix } from 'src/utils/Serenity';
import { LocaleLink, LocaleLinkProps } from '../ContentLink/LocaleLink';

import { ContentLinkV2Props } from './ContentLinkV2';

export interface InternalLinkV2Props extends ContentLinkV2Props<FlatEntry<InternalLink>>, Omit<LocaleLinkProps, 'entry' | 'to'> {
  titleTag?: string;
  dataTestPrefix?: string;
}

export const InternalLinkV2 = React.forwardRef<HTMLAnchorElement, InternalLinkV2Props>(({
  entry,
  children,
  hideTitle,
  dataTestPrefix,
  titleTag = 'span',
  ...props
}, ref) => {
  if (!entry) return null;
  const { page: { slug } = {}, queryParameters, title, hint } = entry;
  if (!slug) return null;

  const { t } = useTranslation();

  const url = getUrlWithParamsOrPdpRedirect(slug, queryParameters);
  const shouldRedirectToPDP = shouldRedirectExistingMerchant(slug);

  const dataTest = testAttributeWithPrefix('internal-link', title, dataTestPrefix);

  const childrenIsValid = isChildrenDefined(children);

  return (
    <LocaleLink
      innerRef={ref}
      to={url}
      title={hint || title}
      data-test={dataTest}
      {...getEntryDataAttributes(entry)}
      {...props}
    >
      {(shouldRedirectToPDP && !childrenIsValid) && React.createElement(titleTag, { 'data-test': 'text' }, t('contact-sales'))}
      {(!shouldRedirectToPDP && !hideTitle && title) && React.createElement(titleTag, { 'data-title': title }, title)}
      {children}
    </LocaleLink>
  );
});

InternalLinkV2.displayName = 'InternalLinkV2';
